import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { formatDisplayDate } from '../../../utils/dateUtils';


const ROW_TYPE_CONFIG = {
  phase: {
    label: 'Phase',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
    rowClass: 'bg-amber-50/60',
    dotClass: 'bg-amber-500',
    icon: 'GitBranch',
  },
  deliverable: {
    label: 'Deliverable',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
    rowClass: 'bg-purple-50/40',
    dotClass: 'bg-purple-500',
    icon: 'Package',
  },
  task: {
    label: 'Task',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
    rowClass: 'bg-blue-50/30',
    dotClass: 'bg-blue-500',
    icon: 'CheckSquare',
  },
  subtask: {
    label: 'Sub-task',
    badgeClass: 'bg-green-100 text-green-800 border-green-200',
    rowClass: 'bg-green-50/20',
    dotClass: 'bg-green-500',
    icon: 'CornerDownRight',
  },
};

const STATUS_CONFIG = {
  pending: { label: 'Not Started', class: 'bg-gray-100 text-gray-700 border-gray-200' },
  'in-progress': { label: 'In Progress', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-700 border-green-200' },
  blocked: { label: 'Blocked', class: 'bg-red-100 text-red-700 border-red-200' },
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return formatDisplayDate(dateString, { month: 'short', day: 'numeric', year: '2-digit' });
};

const formatOrderDisplay = (item) => {
  if (item?.customOrder == null) return '—';
  const n = Number(item?.customOrder);
  if (Number.isInteger(n)) return String(n);
  const parts = n?.toFixed(10)?.replace(/0+$/, '')?.split('.');
  const intPart = parts?.[0];
  const decPart = parts?.[1] || '';
  if (decPart?.length >= 2) {
    return `${intPart}.${decPart?.[0]}.${decPart?.slice(1)}`;
  }
  return `${intPart}.${decPart}`;
};

const ProjectDetailSpreadsheetView = ({ deliverables, resources, phases, project, onEdit, onDelete, onUpdate, onAddChild, onPhaseStatusChange, onAddPhase, onEditPhase, onReorder }) => {
  const projectId = project?.id || 'default';
  const [collapsedIds, setCollapsedIds] = useState(() => {
    try {
      const saved = localStorage.getItem(`spreadsheet_collapsed_ids_${projectId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('defined_order');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [orderEditValue, setOrderEditValue] = useState('');
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemRef = useRef(null);

  const toggleCollapse = (id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const collapseAll = () => {
    const deliverableIds = deliverables
      ?.filter(d => !d?.rowType || d?.rowType === 'deliverable')
      ?.map(d => d?.id);
    setCollapsedIds(new Set(deliverableIds));
  };

  const expandAll = () => setCollapsedIds(new Set());

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(`spreadsheet_collapsed_ids_${projectId}`, JSON.stringify(Array.from(collapsedIds)));
  }, [collapsedIds, projectId]);

  // Sort items
  const sortItems = (items) => {
    if (!items) return [];
    const sorted = [...items];
    if (sortBy === 'defined_order') {
      sorted?.sort((a, b) => {
        const ao = a?.customOrder ?? Infinity;
        const bo = b?.customOrder ?? Infinity;
        if (ao !== bo) return ao - bo;
        return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
      });
    } else if (sortBy === 'start_date') {
      sorted?.sort((a, b) => {
        const ad = a?.startDate ? new Date(a?.startDate) : new Date(9999, 0);
        const bd = b?.startDate ? new Date(b?.startDate) : new Date(9999, 0);
        return ad - bd;
      });
    } else if (sortBy === 'end_date') {
      sorted?.sort((a, b) => {
        const ad = a?.endDate ? new Date(a?.endDate) : new Date(9999, 0);
        const bd = b?.endDate ? new Date(b?.endDate) : new Date(9999, 0);
        return ad - bd;
      });
    }
    return sorted;
  };

  // Build hierarchy with phases at top level
  const buildHierarchy = () => {
    const topLevel = sortItems(deliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable'));
    const tasks = deliverables?.filter(d => d?.rowType === 'task');
    const subtasks = deliverables?.filter(d => d?.rowType === 'subtask');

    if (phases?.length > 0) {
      // Group deliverables by phase
      const phaseRows = phases?.map(phase => ({
        ...phase,
        _isPhase: true,
        rowType: 'phase',
        children: topLevel
          ?.filter(d => d?.phaseId === phase?.id)
          ?.map(del => ({
            ...del,
            children: sortItems(tasks?.filter(t => t?.parentId === del?.id))
              ?.map(task => ({
                ...task,
                children: sortItems(subtasks?.filter(st => st?.parentId === task?.id)),
              })),
          }))
      }));
      // Unphased deliverables
      const unphased = topLevel?.filter(d => !d?.phaseId || !phases?.find(p => p?.id === d?.phaseId));
      if (unphased?.length > 0) {
        phaseRows?.push({
          id: '__unphased__',
          name: 'Unassigned',
          _isPhase: true,
          rowType: 'phase',
          children: unphased?.map(del => ({
            ...del,
            children: sortItems(tasks?.filter(t => t?.parentId === del?.id))
              ?.map(task => ({
                ...task,
                children: sortItems(subtasks?.filter(st => st?.parentId === task?.id)),
              }))
          }))
        });
      }
      return phaseRows;
    }

    return topLevel?.map(del => ({
      ...del,
      children: sortItems(tasks?.filter(t => t?.parentId === del?.id))
        ?.map(task => ({
          ...task,
          children: sortItems(subtasks?.filter(st => st?.parentId === task?.id)),
        })),
    }));
  };

  const hierarchy = buildHierarchy();

  const handleStatusChange = async (item, newStatus) => {
    try { await onUpdate(item?.id, { status: newStatus }); }
    catch (err) { console.error('Error updating status:', err); }
  };

  const handleOrderEditStart = (item) => {
    setEditingOrderId(item?.id);
    setOrderEditValue(item?.customOrder != null ? String(item?.customOrder) : '');
  };

  const handleOrderEditSave = async (item) => {
    const val = parseFloat(orderEditValue);
    if (!isNaN(val)) {
      try { await onUpdate(item?.id, { customOrder: val }); }
      catch (err) { console.error('Error updating order:', err); }
    }
    setEditingOrderId(null);
  };

  const handleOrderKeyDown = (e, item) => {
    if (e?.key === 'Enter') { e?.preventDefault(); handleOrderEditSave(item); }
    if (e?.key === 'Escape') { setEditingOrderId(null); }
  };

  // Drag handlers for row reordering
  const handleDragStart = (e, item) => {
    dragItemRef.current = item;
    e?.dataTransfer?.setData('text/plain', item?.id);
    if (e?.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e?.preventDefault();
    setDragOverId(item?.id);
  };

  const handleDrop = async (e, targetItem) => {
    e?.preventDefault();
    setDragOverId(null);
    const draggedItem = dragItemRef?.current;
    if (!draggedItem || draggedItem?.id === targetItem?.id) return;

    const sameType = (draggedItem?.rowType || 'deliverable') === (targetItem?.rowType || 'deliverable');
    const sameParent = draggedItem?.parentId === targetItem?.parentId;
    if (!sameType || !sameParent) return;

    const siblings = sortItems(deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      const tt = targetItem?.rowType || 'deliverable';
      return dt === tt && d?.parentId === targetItem?.parentId;
    }));

    const dragIdx = siblings?.findIndex(s => s?.id === draggedItem?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetItem?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedItem);

    const rowType = targetItem?.rowType || 'deliverable';
    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (rowType === 'deliverable') {
        newOrder = idx + 1;
      } else if (rowType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (rowType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    await onReorder?.(updates);
    dragItemRef.current = null;
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    dragItemRef.current = null;
  };

  const renderResourceAvatars = (assignedResources) => {
    if (!assignedResources?.length) return <span className="text-xs text-muted-foreground">—</span>;
    return (
      <div className="flex items-center gap-1">
        <div className="flex -space-x-1.5">
          {assignedResources?.slice(0, 3)?.map((r, i) => (
            <div
              key={i}
              title={r?.name}
              className="w-6 h-6 rounded-full border-2 border-card bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium"
            >
              {r?.name?.charAt(0)}
            </div>
          ))}
        </div>
        {assignedResources?.length > 3 && (
          <span className="text-xs text-muted-foreground">+{assignedResources?.length - 3}</span>
        )}
        <span className="text-xs text-muted-foreground ml-1 hidden lg:inline">
          {assignedResources?.slice(0, 2)?.map(r => r?.name?.split(' ')?.[0])?.join(', ')}
          {assignedResources?.length > 2 ? '...' : ''}
        </span>
      </div>
    );
  };

  const renderRow = (item, depth = 0) => {
    const rowType = item?.rowType || 'deliverable';
    const typeConfig = ROW_TYPE_CONFIG?.[rowType] || ROW_TYPE_CONFIG?.deliverable;
    const statusConfig = STATUS_CONFIG?.[item?.status] || STATUS_CONFIG?.pending;
    const isCollapsed = collapsedIds?.has(item?.id);
    const hasChildren = item?.children?.length > 0;
    const isDragOver = dragOverId === item?.id;

    // Phase rows - special rendering
    if (item?._isPhase) {
      return (
        <React.Fragment key={item?.id}>
          <tr className={`border-b border-border ${typeConfig?.rowClass}`}>
            <td className="px-3 py-2.5 min-w-[220px]" colSpan={1}>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => hasChildren && toggleCollapse(item?.id)}
                  className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded transition-colors ${
                    hasChildren ? 'hover:bg-muted cursor-pointer text-muted-foreground' : 'cursor-default text-transparent'
                  }`}
                >
                  {hasChildren && (
                    <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={13} />
                  )}
                </button>
                <Icon name="GitBranch" size={14} className="text-amber-600 flex-shrink-0" />
                <span className="text-sm font-bold text-foreground">{item?.name}</span>
                {item?.id !== '__unphased__' && item?.status && onPhaseStatusChange ? (
                  <select
                    value={item?.status || 'Planning'}
                    onChange={(e) => onPhaseStatusChange(item?.id, e?.target?.value, item?.name)}
                    onClick={(e) => e?.stopPropagation()}
                    className={`ml-1 text-xs font-medium px-2 py-0.5 rounded border cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring ${
                      item?.status === 'Completed' ? 'bg-green-100 text-green-700 border-green-200' :
                      item?.status === 'In Progress' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      item?.status === 'On Hold'? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-amber-100 text-amber-800 border-amber-200'
                    }`}
                  >
                    <option value="Planning">Planning</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                ) : item?.id !== '__unphased__' && item?.status && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium border ${typeConfig?.badgeClass}`}>
                    {item?.status}
                  </span>
                )}
                <span className="text-xs text-muted-foreground ml-1">({item?.children?.length || 0} deliverables)</span>

                {/* Phase action buttons */}
                {item?.id !== '__unphased__' && (
                  <div className="flex items-center gap-1 ml-auto">
                    <button
                      onClick={(e) => { e?.stopPropagation(); onAddPhase && onAddPhase(); }}
                      title="Add new phase"
                      className="p-1 rounded hover:bg-amber-200 text-amber-700 transition-colors flex items-center gap-1"
                    >
                      <Icon name="Plus" size={13} />
                      <span className="text-xs font-medium hidden sm:inline">Add Phase</span>
                    </button>
                    <button
                      onClick={(e) => { e?.stopPropagation(); onEditPhase && onEditPhase(item); }}
                      title="Edit phase"
                      className="p-1 rounded hover:bg-amber-200 text-amber-700 transition-colors flex items-center gap-1"
                    >
                      <Icon name="Pencil" size={13} />
                      <span className="text-xs font-medium hidden sm:inline">Edit</span>
                    </button>
                  </div>
                )}
              </div>
            </td>
            {/* Open button cell - empty for phase rows */}
            <td className="px-2 py-2.5 whitespace-nowrap"></td>
            <td colSpan={8} className="px-3 py-2.5">
              {item?.id !== '__unphased__' && item?.startDate && (
                <span className="text-xs text-muted-foreground">
                  {formatDate(item?.startDate)} → {formatDate(item?.endDate)}
                </span>
              )}
            </td>
          </tr>
          {!isCollapsed && hasChildren && (
            item?.children?.map(child => renderRow(child, depth + 1))
          )}
        </React.Fragment>
      );
    }

    // Apply filters
    if (filterType !== 'all' && rowType !== filterType) return null;
    if (filterStatus !== 'all' && item?.status !== filterStatus) return null;

    const indentPx = depth * 24;

    return (
      <React.Fragment key={item?.id}>
        <tr
          draggable
          onDragStart={(e) => handleDragStart(e, item)}
          onDragOver={(e) => handleDragOver(e, item)}
          onDrop={(e) => handleDrop(e, item)}
          onDragEnd={handleDragEnd}
          className={`border-b border-border hover:bg-muted/30 transition-colors group ${typeConfig?.rowClass} ${
            isDragOver ? 'border-t-2 border-t-primary' : ''
          }`}
        >
          {/* Name column */}
          <td className="px-3 py-2.5 min-w-[220px]">
            <div className="flex items-center gap-1" style={{ paddingLeft: `${indentPx}px` }}>
              {/* Drag handle */}
              <span className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                <Icon name="GripVertical" size={13} />
              </span>
              {/* Collapse toggle */}
              <button
                onClick={() => hasChildren && toggleCollapse(item?.id)}
                className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded transition-colors ${
                  hasChildren ? 'hover:bg-muted cursor-pointer text-muted-foreground' : 'cursor-default text-transparent'
                }`}
              >
                {hasChildren && (
                  <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={13} />
                )}
              </button>

              {/* Type icon */}
              <span className={`flex-shrink-0 w-4 h-4 flex items-center justify-center`}>
                <Icon name={typeConfig?.icon} size={13} className={`${
                  rowType === 'deliverable' ? 'text-purple-600' : rowType === 'task' ? 'text-blue-600' : 'text-green-600'
                }`} />
              </span>

              {/* Name */}
              <span className={`text-sm font-medium text-foreground truncate ${
                rowType === 'deliverable' ? 'font-semibold' : ''
              }`}>
                {item?.name}
              </span>
            </div>
          </td>

          {/* Open button column */}
          <td className="px-2 py-2.5 whitespace-nowrap">
            <button
              onClick={() => onEdit(item)}
              title="Open deliverable"
              className="p-1 rounded hover:bg-primary/10 text-primary transition-colors"
            >
              <Icon name="ExternalLink" size={13} />
            </button>
          </td>

          {/* Type badge */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${typeConfig?.badgeClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${typeConfig?.dotClass}`}></span>
              {typeConfig?.label}
            </span>
          </td>

          {/* Status */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            <select
              value={item?.status || 'pending'}
              onChange={(e) => handleStatusChange(item, e?.target?.value)}
              className={`text-xs font-medium px-3 py-1 rounded-full border cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring ${statusConfig?.class}`}
            >
              <option value="pending">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
          </td>

          {/* Order column — after Status, before Start Date */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            {editingOrderId === item?.id ? (
              <input
                type="number"
                step="0.001"
                autoFocus
                value={orderEditValue}
                onChange={(e) => setOrderEditValue(e?.target?.value)}
                onBlur={() => handleOrderEditSave(item)}
                onKeyDown={(e) => handleOrderKeyDown(e, item)}
                className="w-20 text-xs font-mono border border-primary rounded px-1.5 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            ) : (
              <button
                onClick={() => handleOrderEditStart(item)}
                title="Click to set order"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted border border-border text-xs font-mono font-medium text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
              >
                <Icon name="Hash" size={10} />
                {formatOrderDisplay(item)}
              </button>
            )}
          </td>

          {/* Start Date */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            <span className="text-xs text-foreground">{formatDate(item?.startDate)}</span>
          </td>

          {/* End Date */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            <span className="text-xs text-foreground">{formatDate(item?.endDate)}</span>
          </td>

          {/* Work Estimate */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            {item?.workEstimate || item?.estimatedHours ? (
              <span className="text-xs text-foreground flex items-center gap-1">
                <Icon name="Clock" size={12} className="text-muted-foreground" />
                {item?.workEstimate || `${item?.estimatedHours}h`}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </td>

          {/* Assigned Resources */}
          <td className="px-3 py-2.5">
            {renderResourceAvatars(item?.assignedResources)}
          </td>

          {/* Actions */}
          <td className="px-3 py-2.5 whitespace-nowrap">
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {rowType === 'deliverable' && (
                <button
                  onClick={() => onAddChild(item, 'task')}
                  title="Add Task"
                  className="p-1 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                >
                  <Icon name="Plus" size={13} />
                </button>
              )}
              {rowType === 'task' && (
                <button
                  onClick={() => onAddChild(item, 'subtask')}
                  title="Add Sub-task"
                  className="p-1 rounded hover:bg-green-100 text-green-600 transition-colors"
                >
                  <Icon name="Plus" size={13} />
                </button>
              )}
              <button
                onClick={() => onEdit(item)}
                title="Edit"
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="Edit2" size={13} />
              </button>
              <button
                onClick={() => onDelete(item?.id)}
                title="Delete"
                className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
              >
                <Icon name="Trash2" size={13} />
              </button>
            </div>
          </td>
        </tr>
        {/* Children */}
        {!isCollapsed && hasChildren && (
          item?.children?.map(child => renderRow(child, depth + 1))
        )}
      </React.Fragment>
    );
  };

  if (deliverables?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Table" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Items Yet</h3>
        <p className="text-sm text-muted-foreground">Get started by adding your first deliverable to this project.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-card rounded-lg border border-border p-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                className="px-3 py-1.5 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Types</option>
                <option value="deliverable">Deliverables</option>
                <option value="task">Tasks</option>
                <option value="subtask">Sub-tasks</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-3 py-1.5 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-1.5 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="defined_order">Defined Order</option>
                <option value="start_date">Start Date</option>
                <option value="end_date">End Date</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronsDown" size={13} />
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronsUp" size={13} />
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Summary counts */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-purple-500"></span>
          {deliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable')?.length} Deliverables
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          {deliverables?.filter(d => d?.rowType === 'task')?.length} Tasks
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          {deliverables?.filter(d => d?.rowType === 'subtask')?.length} Sub-tasks
        </span>
      </div>

      {/* Spreadsheet Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="bg-muted/60 border-b border-border">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[220px]">
                  Name
                </th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-8">
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  Type
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Status
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  Order
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  Start Date
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  End Date
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  Work Est.
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[160px]">
                  Resources
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filterType === 'all'
                ? hierarchy?.map(item => renderRow(item, 0))
                : sortItems(deliverables
                    ?.filter(d => filterType === 'all' || (d?.rowType || 'deliverable') === filterType)
                    ?.filter(d => filterStatus === 'all' || d?.status === filterStatus))
                    ?.map(item => renderRow(item, 0))
              }
            </tbody>
          </table>
        </div>

        {hierarchy?.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="Filter" size={32} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No items match your current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailSpreadsheetView;
